import type { SeacloudConfig, TaskResult } from './types.js';
import { SeacloudError } from './types.js';
import { createConfig, validateConfig, getApiToken, checkAndNotifyInsufficientBalance, checkResponseBodyForInsufficientBalance } from './config.js';
import { VERSION } from './version.js';
import { getCustomHeaders } from './global-config.js';

/**
 * SeaCloud HTTP 客户端
 * 用于创建任务和查询任务状态
 */
export class SeacloudClient {
  private config: Required<SeacloudConfig>;
  private providedApiKey?: string; // 保存用户提供的 apiKey

  private constructor(config: Required<SeacloudConfig>, providedApiKey?: string) {
    this.config = config;
    this.providedApiKey = providedApiKey;
    validateConfig(this.config);

    // 检测是否为浏览器环境
    const isBrowser = typeof (globalThis as any).window !== 'undefined' &&
                      typeof (globalThis as any).localStorage !== 'undefined';

    // 仅在浏览器环境打印调试信息
    if (isBrowser) {
      console.log('[SeaCloud SDK] Client initialized', {
        version: VERSION,
        environment: 'browser',
        tokenSource: 'localStorage.auth_token',
        baseUrl: this.config.baseUrl,
        timeout: this.config.timeout,
        hasCustomApiKey: !!this.providedApiKey,
      });
    }
  }

  /**
   * 创建 SeacloudClient 实例（异步工厂方法）
   * @param config 配置选项
   * @returns SeacloudClient 实例
   */
  static async create(config: SeacloudConfig = {}): Promise<SeacloudClient> {
    const fullConfig = await createConfig(config);
    return new SeacloudClient(fullConfig, config.apiKey);
  }

  /**
   * 创建一个新任务
   * @param endpoint API 端点路径（例如：/model/tasks）
   * @param body 请求体
   * @returns 任务结果
   */
  async createTask(endpoint: string, body: Record<string, any>): Promise<TaskResult> {
    const url = `${this.config.baseUrl}${endpoint}`;

    // 每次请求时动态获取最新的 token（现在是异步的）
    const currentToken = await getApiToken(this.providedApiKey);

    // 获取全局自定义 headers
    const customHeaders = getCustomHeaders();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await this.config.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
          ...customHeaders, // 合并自定义 headers
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorBody = await response.text();
        const err = new SeacloudError(
          `HTTP ${response.status}: ${errorBody}`,
          response.status,
          errorBody,
        );
        checkAndNotifyInsufficientBalance(err);
        throw err;
      }

      const result = await response.json();
      checkResponseBodyForInsufficientBalance(result);
      return result as TaskResult;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof SeacloudError) {
        checkAndNotifyInsufficientBalance(error);
        throw error;
      }

      if ((error as Error).name === 'AbortError') {
        throw new SeacloudError(`Request timeout after ${this.config.timeout}ms`);
      }

      throw new SeacloudError(
        `Request failed: ${(error as Error).message}`,
        undefined,
        error,
      );
    }
  }

  /**
   * 查询任务状态
   * @param endpoint API 端点路径（例如：/model/v1/generation）
   * @param taskId 任务 ID
   * @returns 任务结果
   */
  async getTaskStatus(endpoint: string, taskId: string): Promise<TaskResult> {
    const url = `${this.config.baseUrl}${endpoint}/task/${taskId}`;

    // 每次请求时动态获取最新的 token（现在是异步的）
    const currentToken = await getApiToken(this.providedApiKey);

    // 获取全局自定义 headers
    const customHeaders = getCustomHeaders();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await this.config.fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          ...customHeaders, // 合并自定义 headers
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorBody = await response.text();
        const err = new SeacloudError(
          `HTTP ${response.status}: ${errorBody}`,
          response.status,
          errorBody,
        );
        checkAndNotifyInsufficientBalance(err);
        throw err;
      }

      const result = await response.json();
      checkResponseBodyForInsufficientBalance(result);
      return result as TaskResult;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof SeacloudError) {
        checkAndNotifyInsufficientBalance(error);
        throw error;
      }

      if ((error as Error).name === 'AbortError') {
        throw new SeacloudError(`Request timeout after ${this.config.timeout}ms`);
      }

      throw new SeacloudError(
        `Request failed: ${(error as Error).message}`,
        undefined,
        error,
      );
    }
  }

  /**
   * 获取当前配置
   */
  getConfig(): Readonly<Required<SeacloudConfig>> {
    return { ...this.config };
  }
}
